import React, { useState, useEffect, useCallback } from "react";
import {
  FaRobot,
  FaPlus,
  FaTrash,
  FaPlay,
  FaPause,
  FaPaperPlane,
  FaMagic,
  FaChevronRight,
  FaChevronDown,
  FaCalendarAlt,
  FaCheck,
} from "react-icons/fa";
import {
  Card,
  CardBody,
  CardHeader,
  Badge,
  Button,
  Input,
  InputGroup,
  Select,
  Spinner,
  EmptyState,
} from "../ui";
import { apiGet, apiPost, apiPut, apiDelete } from "../../utils/api";
import { useModal } from "../../context/ModalContext";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const TIMEZONES =
  typeof Intl !== "undefined" && Intl.supportedValuesOf
    ? Intl.supportedValuesOf("timeZone")
    : ["UTC", "Asia/Kolkata", "Asia/Dubai", "Europe/London", "America/New_York"];

function guessTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function scheduleLabel(r) {
  if (r.frequency === "interval") {
    const m = Number(r.intervalMinutes) || 60;
    if (m % 1440 === 0) return `every ${m / 1440} day(s)`;
    if (m % 60 === 0) return `every ${m / 60} hour(s)`;
    return `every ${m} min`;
  }
  if (r.frequency === "weekly") return `${DAYS[r.dayOfWeek] || "Monday"} at ${r.time}`;
  return `daily at ${r.time}`;
}

function statusVariant(status) {
  if (status === "active") return "success";
  if (status === "paused") return "warning";
  if (status === "draft") return "info";
  return "neutral";
}

/* ── Routine card (used in review + on an existing takeover) ── */
function RoutineCard({ routine, onChange, onDelete, compact = false }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-surface-border rounded-lg bg-surface-card">
      <div className="flex items-center justify-between px-3 py-2.5 gap-2">
        <button
          type="button"
          className="flex items-center gap-2 min-w-0 text-left flex-1"
          onClick={() => setOpen((o) => !o)}
        >
          <FaCalendarAlt size={11} className="text-sky-600 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-ink-primary truncate">{routine.title}</p>
            <p className="text-2xs text-ink-muted">
              {scheduleLabel(routine)}
              {routine.message ? " · fixed text" : " · AI writes it"}
            </p>
          </div>
          {open ? <FaChevronDown size={10} className="text-ink-faint" /> : <FaChevronRight size={10} className="text-ink-faint" />}
        </button>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <label className="flex items-center gap-1 text-2xs text-ink-muted cursor-pointer">
            <input
              type="checkbox"
              className="accent-sky-500"
              checked={routine.enabled !== false}
              onChange={(e) => onChange({ ...routine, enabled: e.target.checked })}
            />
            On
          </label>
          {onDelete && (
            <button className="text-ink-faint hover:text-red-500 p-1" onClick={onDelete}>
              <FaTrash size={10} />
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="px-3 pb-3 pt-1 space-y-2.5 border-t border-surface-border">
          <InputGroup label="Title">
            <Input value={routine.title} onChange={(e) => onChange({ ...routine, title: e.target.value })} />
          </InputGroup>
          <InputGroup label="What the AI should do">
            <textarea
              className="input min-h-[60px]"
              value={routine.instruction}
              onChange={(e) => onChange({ ...routine, instruction: e.target.value })}
            />
          </InputGroup>
          <InputGroup label="Fixed message (optional)" hint="Leave empty and the AI writes a fresh post each time from the instruction.">
            <textarea
              className="input min-h-[50px]"
              placeholder="Leave empty for AI-written posts"
              value={routine.message || ""}
              onChange={(e) => onChange({ ...routine, message: e.target.value })}
            />
          </InputGroup>
          <div className="grid grid-cols-2 gap-2.5">
            <InputGroup label="Repeats">
              <Select value={routine.frequency} onChange={(e) => onChange({ ...routine, frequency: e.target.value })}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="interval">Interval</option>
              </Select>
            </InputGroup>
            {routine.frequency === "weekly" && (
              <InputGroup label="Day">
                <Select
                  value={routine.dayOfWeek}
                  onChange={(e) => onChange({ ...routine, dayOfWeek: Number(e.target.value) })}
                >
                  {DAYS.map((d, i) => (
                    <option key={d} value={i}>{d}</option>
                  ))}
                </Select>
              </InputGroup>
            )}
            {routine.frequency === "interval" && (
              <InputGroup label="Every (minutes)">
                <Input
                  type="number"
                  min="1"
                  value={routine.intervalMinutes}
                  onChange={(e) => onChange({ ...routine, intervalMinutes: Number(e.target.value) })}
                />
              </InputGroup>
            )}
            {routine.frequency !== "interval" && (
              <InputGroup label="At (time)">
                <Input
                  type="time"
                  value={routine.time}
                  onChange={(e) => onChange({ ...routine, time: e.target.value })}
                />
              </InputGroup>
            )}
          </div>
          {routine.frequency !== "interval" && (
            <InputGroup label="Timezone">
              <Select value={routine.timezone} onChange={(e) => onChange({ ...routine, timezone: e.target.value })}>
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </Select>
            </InputGroup>
          )}
        </div>
      )}
    </div>
  );
}

/* ── The conversational create wizard ── */
function TakeoverWizard({ sessions, onCancel, onCreated }) {
  const modal = useModal();
  const [step, setStep] = useState("goal"); // goal | questions | review
  const [sessionId, setSessionId] = useState(sessions[0]?.sessionId || "");
  const [goal, setGoal] = useState("");
  const [planning, setPlanning] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [summary, setSummary] = useState("");
  const [persona, setPersona] = useState("");
  const [replyToAll, setReplyToAll] = useState(true);
  const [replyKeywords, setReplyKeywords] = useState("");
  const [routines, setRoutines] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupIds, setGroupIds] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setGroups([]);
      return;
    }
    let cancelled = false;
    setGroupsLoading(true);
    apiGet(`/api/groups/${sessionId}`)
      .then((res) => {
        if (cancelled) return;
        const data = Array.isArray(res) ? res : res?.data || res?.groups || [];
        setGroups(Array.isArray(data) ? data : []);
      })
      .catch(() => !cancelled && setGroups([]))
      .finally(() => !cancelled && setGroupsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const callPlanner = async (extraAnswers = []) => {
    setPlanning(true);
    try {
      const res = await apiPost("/api/ai/takeover-plan", {
        objective: goal,
        answers: extraAnswers,
        sessionId,
        groupNames: groups.filter((g) => groupIds.includes(g.id)).map((g) => g.name),
      });
      if (!res?.success) {
        modal.alert(res?.message || "Could not build a plan", "error");
        return;
      }
      if (res.needsMoreInfo && res.questions?.length) {
        setQuestions(res.questions.map((q) => ({ ...q, answer: "" })));
        setStep("questions");
        return;
      }
      setSummary(res.summary || "");
      setPersona(res.persona || "");
      setReplyToAll(res.replyToAll !== false);
      setReplyKeywords((res.replyKeywords || []).join(", "));
      setRoutines(
        (res.routines || []).map((r) => ({
          ...r,
          enabled: true,
          timezone: r.timezone && r.timezone !== "UTC" ? r.timezone : guessTimezone(),
        }))
      );
      setStep("review");
    } catch (err) {
      modal.alert(err?.message || "Planner failed", "error");
    } finally {
      setPlanning(false);
    }
  };

  const submitQuestions = () => {
    const extra = questions
      .filter((q) => q.answer.trim())
      .map((q) => ({ question: q.question, answer: q.answer.trim() }));
    callPlanner(extra);
  };

  const create = async (activate) => {
    if (!sessionId || !goal.trim()) {
      modal.alert("Session and goal are required.", "error");
      return;
    }
    if (routines.length === 0) {
      modal.alert("Add at least one routine.", "error");
      return;
    }
    setSaving(true);
    try {
      const selectedNames = groups.filter((g) => groupIds.includes(g.id)).map((g) => g.name);
      const createRes = await apiPost("/api/ai-takeovers", {
        name: goal.trim().slice(0, 60),
        sessionId,
        objective: goal.trim(),
        persona,
        setupNotes: summary,
        groupIds,
        groupNames: selectedNames,
        replyToAll,
        replyKeywords: replyKeywords.split(",").map((k) => k.trim()).filter(Boolean),
        routines,
      });
      if (!createRes?.success) {
        modal.alert(createRes?.message || "Failed to create takeover", "error");
        return;
      }
      const id = createRes.data.id;
      if (activate) {
        const actRes = await apiPost(`/api/ai-takeovers/${id}/activate`, {});
        if (!actRes?.success) {
          modal.alert(actRes?.message || "Created, but activation failed.", "error");
        }
      }
      onCreated();
    } catch (err) {
      modal.alert(err?.message || "Failed to create takeover", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-surface-border rounded-xl bg-surface-bg/40 p-4 space-y-4">
      {/* Step: goal */}
      {step === "goal" && (
        <>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-fuchsia-100 flex items-center justify-center flex-shrink-0">
              <FaMagic size={13} className="text-fuchsia-600" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-ink-primary">What should your AI do?</h4>
              <p className="text-xs text-ink-muted">
                Describe the outcome in your own words. The AI will ask a couple of questions if it
                needs to, then propose scheduled routines you can review.
              </p>
            </div>
          </div>

          <InputGroup label="WhatsApp Session">
            <Select value={sessionId} onChange={(e) => { setSessionId(e.target.value); setGroupIds([]); }}>
              <option value="">Select a connected session</option>
              {sessions.map((s) => (
                <option key={s.sessionId} value={s.sessionId}>{s.number || s.sessionId}</option>
              ))}
            </Select>
          </InputGroup>

          <InputGroup label="Your goal">
            <textarea
              className="input min-h-[90px]"
              placeholder="e.g. Keep our VIP customers group engaged with a weekly tip, a Monday motivation post, and answer any pricing questions quickly."
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
            />
          </InputGroup>

          <div className="flex items-center gap-2">
            <Button variant="primary" size="sm" onClick={() => callPlanner([])} loading={planning} disabled={!sessionId || !goal.trim()}>
              <FaMagic size={10} className="mr-1.5" /> Build my plan
            </Button>
            <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
          </div>
        </>
      )}

      {/* Step: questions */}
      {step === "questions" && (
        <>
          <div className="flex items-center gap-2">
            <FaRobot size={13} className="text-fuchsia-600" />
            <h4 className="text-sm font-semibold text-ink-primary">A few quick questions</h4>
          </div>
          <div className="space-y-3">
            {questions.map((q, i) => (
              <InputGroup key={i} label={q.question} hint={q.hint}>
                <Input
                  value={q.answer}
                  onChange={(e) =>
                    setQuestions((prev) => prev.map((x, idx) => (idx === i ? { ...x, answer: e.target.value } : x)))
                  }
                />
              </InputGroup>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="primary" size="sm" onClick={submitQuestions} loading={planning}>
              <FaChevronRight size={10} className="mr-1.5" /> Continue
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setStep("goal")}>Back</Button>
          </div>
        </>
      )}

      {/* Step: review */}
      {step === "review" && (
        <>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <FaCheck size={13} className="text-emerald-600" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-ink-primary">Here's the plan</h4>
              {summary && <p className="text-xs text-ink-muted">{summary}</p>}
            </div>
          </div>

          <InputGroup label="Groups to manage" hint="Leave empty to apply to every group this number is in.">
            {groupsLoading ? (
              <div className="flex items-center gap-2 text-ink-muted text-sm py-1"><Spinner size="sm" /> Loading groups…</div>
            ) : (
              <div className="max-h-40 overflow-y-auto border border-surface-border rounded-lg divide-y divide-surface-border">
                {groups.length === 0 ? (
                  <p className="text-xs text-ink-muted p-3">No groups found for this session.</p>
                ) : (
                  groups.map((g) => {
                    const checked = groupIds.includes(g.id);
                    return (
                      <label key={g.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer ${checked ? "bg-emerald-50" : "hover:bg-surface-bg"}`}>
                        <input
                          type="checkbox"
                          className="accent-emerald-500"
                          checked={checked}
                          onChange={() =>
                            setGroupIds((prev) => (checked ? prev.filter((id) => id !== g.id) : [...prev, g.id]))
                          }
                        />
                        <span className="text-xs font-medium text-ink-primary truncate">{g.name}</span>
                        <span className="text-2xs text-ink-muted ml-auto">{g.size}</span>
                      </label>
                    );
                  })
                )}
              </div>
            )}
          </InputGroup>

          <InputGroup label="AI voice / guardrails">
            <textarea
              className="input min-h-[60px]"
              value={persona}
              onChange={(e) => setPersona(e.target.value)}
            />
          </InputGroup>

          <InputGroup label="Reply policy">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" className="accent-fuchsia-500" checked={replyToAll} onChange={(e) => setReplyToAll(e.target.checked)} />
                Answer every message in scope
              </label>
              {!replyToAll && (
                <Input
                  placeholder="Only reply when message contains… (comma separated)"
                  value={replyKeywords}
                  onChange={(e) => setReplyKeywords(e.target.value)}
                />
              )}
            </div>
          </InputGroup>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="input-label mb-0">Routines ({routines.length})</label>
              <button
                type="button"
                className="text-xs text-brand hover:underline"
                onClick={() =>
                  setRoutines((prev) => [
                    ...prev,
                    {
                      title: "New routine",
                      instruction: "",
                      message: "",
                      frequency: "weekly",
                      dayOfWeek: 1,
                      time: "09:00",
                      timezone: guessTimezone(),
                      intervalMinutes: 10080,
                      followUpMinutes: 0,
                      enabled: true,
                    },
                  ])
                }
              >
                + Add routine
              </button>
            </div>
            <div className="space-y-2">
              {routines.map((r, i) => (
                <RoutineCard
                  key={i}
                  routine={r}
                  onChange={(next) => setRoutines((prev) => prev.map((x, idx) => (idx === i ? next : x)))}
                  onDelete={() => setRoutines((prev) => prev.filter((_, idx) => idx !== i))}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <Button variant="primary" size="sm" onClick={() => create(true)} loading={saving}>
              <FaPlay size={10} className="mr-1.5" /> Create &amp; activate
            </Button>
            <Button variant="secondary" size="sm" onClick={() => create(false)} disabled={saving}>
              Save as draft
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setStep("goal")}>Back</Button>
          </div>
        </>
      )}
    </div>
  );
}

export default function AITakeover({ sessions = [] }) {
  const modal = useModal();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet("/api/ai-takeovers");
      setRows(res?.data || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const setStatus = async (row, action) => {
    const res = await apiPost(`/api/ai-takeovers/${row.id}/${action}`, {});
    if (res?.success) fetchRows();
    else modal.alert(res?.message || `Failed to ${action}`, "error");
  };

  const handleDelete = async (row) => {
    if (!(await modal.confirm(`Delete takeover "${row.name}" and all its routines?`, { danger: true, confirmText: "Delete" }))) return;
    const res = await apiDelete(`/api/ai-takeovers/${row.id}`);
    if (res?.success) fetchRows();
  };

  const toggleRoutine = async (takeover, routine, enabled) => {
    const res = await apiPut(`/api/ai-takeovers/${takeover.id}/routines/${routine.id}`, { enabled });
    if (res?.success) fetchRows();
  };

  const runRoutineNow = async (takeover, routine) => {
    const res = await apiPost(`/api/ai-takeovers/${takeover.id}/routines/${routine.id}/run`, {});
    if (res?.success) modal.alert("Routine posted.", "success");
    else modal.alert(res?.message || "Failed to run routine", "error");
  };

  return (
    <Card className="mt-6">
      <CardHeader className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FaRobot size={13} className="text-fuchsia-600" />
          <span className="font-semibold text-ink-primary text-sm">AI Takeover</span>
        </div>
        <Button variant="primary" size="sm" onClick={() => setShowWizard((s) => !s)}>
          <FaPlus size={10} className="mr-1.5" /> New Takeover
        </Button>
      </CardHeader>

      <CardBody>
        <p className="text-xs text-ink-muted mb-4">
          Tell the AI what you want in plain language. It sets up recurring routines, posts on
          schedule, and answers group messages on its own — standing down the moment a teammate replies.
        </p>

        {showWizard && (
          <div className="mb-4">
            <TakeoverWizard
              sessions={sessions}
              onCancel={() => setShowWizard(false)}
              onCreated={() => { setShowWizard(false); fetchRows(); }}
            />
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-ink-muted py-4">
            <Spinner size="sm" /> Loading takeovers…
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={FaRobot}
            title="No AI takeovers yet"
            description="Describe what you want your AI to do and it will build the routines for you."
          />
        ) : (
          <div className="space-y-3">
            {rows.map((row) => (
              <div key={row.id} className="border border-surface-border rounded-lg p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-ink-primary truncate">{row.name}</p>
                      <Badge variant={statusVariant(row.status)}>{row.status}</Badge>
                      <Badge variant="neutral">groups</Badge>
                    </div>
                    <p className="text-xs text-ink-muted mt-0.5 line-clamp-2">{row.objective}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {row.status === "active" ? (
                      <button className="btn-secondary btn-sm flex items-center gap-1" onClick={() => setStatus(row, "pause")}>
                        <FaPause size={10} /> Pause
                      </button>
                    ) : (
                      <button className="btn-primary btn-sm flex items-center gap-1" onClick={() => setStatus(row, "activate")}>
                        <FaPlay size={10} /> Activate
                      </button>
                    )}
                    <button className="btn-danger btn-sm btn-icon" onClick={() => handleDelete(row)}>
                      <FaTrash size={10} />
                    </button>
                  </div>
                </div>

                {(row.routines || []).length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {row.routines.map((r) => (
                      <div key={r.id} className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md bg-surface-bg">
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-ink-primary truncate">{r.title}</p>
                          <p className="text-2xs text-ink-muted">{scheduleLabel(r)}{r.enabled ? "" : " · off"}</p>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button
                            className="text-ink-faint hover:text-brand p-1"
                            title="Run now"
                            onClick={() => runRoutineNow(row, r)}
                          >
                            <FaPaperPlane size={10} />
                          </button>
                          <label className="flex items-center gap-1 text-2xs text-ink-muted cursor-pointer">
                            <input
                              type="checkbox"
                              className="accent-fuchsia-500"
                              checked={r.enabled}
                              onChange={(e) => toggleRoutine(row, r, e.target.checked)}
                            />
                            On
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
